import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../contexts/AuthContext';
import { companiesAPI, plansAPI, industriesAPI } from '../services/api';
import { countriesAPI, currenciesAPI } from '../services/mastersAPI';
import DataTable from '../components/DataTable';
import Layout from '../components/layout/Layout';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  UsersIcon,
  CurrencyDollarIcon,
  CloudArrowUpIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const Companies = () => {
  // Helper function to format address
  const formatAddress = (address) => {
    if (!address) return '';
    if (typeof address === 'string') return address;
    
    return [
      address.street,
      address.city,
      address.state,
      address.zipCode,
      address.country
    ].filter(Boolean).join(', ');
  };

  // Helper function to format storage size
  const formatStorageSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const k = 1024;
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${units[i]}`;
  };

  const [companies, setCompanies] = useState([]);
  const [plans, setPlans] = useState([]);
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [industryCustomFields, setIndustryCustomFields] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    country: '',
    currency: '',
    industry: '',
    plan: '',
    subscriptionStatus: 'active',
    description: '',
    customFields: []
  });

  useEffect(() => {
    fetchCompanies();
    fetchPlans();
    fetchCountries();
    fetchCurrencies();
    fetchIndustries();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await plansAPI.getActive();
      setPlans(response.data || response);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Failed to load plans');
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await countriesAPI.getAllCountries();
      setCountries(response);
    } catch (error) {
      console.error('Error fetching countries:', error);
      toast.error('Failed to load countries');
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await currenciesAPI.getAllCurrencies();
      setCurrencies(response);
    } catch (error) {
      console.error('Error fetching currencies:', error);
      toast.error('Failed to load currencies');
    }
  };

  const fetchIndustries = async () => {
    try {
      const response = await industriesAPI.getAll();
      // Support multiple possible response shapes from API:
      // - response.data.industries (IndustryMaster returns this)
      // - response.data.data (some endpoints use `data` wrapper)
      // - response.data (direct array)
      const list = response?.data?.industries || response?.data?.data || response?.data || response;
      setIndustries(list || []);
    } catch (error) {
      console.error('Error fetching industries:', error);
      toast.error('Failed to load industries');
      setIndustries([]);
    }
  };

  const handleIndustryChange = async (industryId) => {
    // Update selected industry id in form
    setFormData({ ...formData, industry: industryId });

    if (industryId) {
      try {
        // Fetch the selected industry's custom fields
        const response = await industriesAPI.getById(industryId);
        const industry = response.data.industry || response.data;
        const fetched = industry.customFields || [];

        // Map industry fields into the client form shape and mark as `isIndustry`
        const mapped = fetched.map(f => ({
          // Support both shapes coming from API
          name: f.name || f.fieldName || '',
          type: f.type || f.fieldType || 'text',
          required: f.required || f.isRequired || false,
          options: Array.isArray(f.options) ? f.options : (f.options ? [f.options] : []),
          order: f.order ?? f.displayOrder ?? 0,
          module: f.module || 'lead', // Include module field with default fallback
          isIndustry: true
        }));

        setIndustryCustomFields(mapped);

        // Merge industry fields into the beginning of formData.customFields
        // Keep any existing company-specific custom fields (non-industry)
        const companyFields = (formData.customFields || []).filter(cf => !cf.isIndustry);
        setFormData(prev => ({ ...prev, customFields: [...mapped, ...companyFields] }));
      } catch (error) {
        console.error('Error fetching industry custom fields:', error);
        setIndustryCustomFields([]);
        toast.error('Failed to load industry custom fields');
      }
    } else {
      setIndustryCustomFields([]);
      // Remove any previously merged industry fields from form data
      setFormData(prev => ({ ...prev, customFields: (prev.customFields || []).filter(cf => !cf.isIndustry) }));
    }
  };

  // Filter plans by selected country
  useEffect(() => {
    if (formData.country && plans.length > 0) {
      const filtered = plans.filter(plan => 
        plan.country?._id === formData.country || plan.country === formData.country
      );
      setFilteredPlans(filtered);
      
      // Reset plan selection if current plan is not available for selected country
      if (formData.plan && !filtered.find(p => p._id === formData.plan)) {
        setFormData(prev => ({ ...prev, plan: '' }));
      }
    } else {
      setFilteredPlans(plans);
    }
  }, [formData.country, plans]);

  const handleCountryChange = (countryId) => {
    const selectedCountry = countries.find(country => country._id === countryId);
    if (selectedCountry) {
      // Auto-select currency based on country
      const countryCurrency = currencies.find(currency => currency.code === selectedCountry.currency);
      setFormData({
        ...formData,
        country: countryId,
        currency: countryCurrency ? countryCurrency._id : formData.currency
      });
    } else {
      setFormData({
        ...formData,
        country: countryId
      });
    }
  };

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await companiesAPI.getAll();
      const companiesData = response.data.companies || response.data || [];
      
      // Map the data to include storageUsed from usage and resolve country/currency
      const mappedCompanies = companiesData.map(company => {
        const resolvedCountry = countries.find(c => c._id === company.country) || 
                               (typeof company.country === 'object' ? company.country : null);
        const resolvedCurrency = currencies.find(c => c._id === company.currency) || 
                                (typeof company.currency === 'object' ? company.currency : null);
        
        return {
          ...company,
          country: resolvedCountry,
          currency: resolvedCurrency,
          storageUsed: company.usage ? company.usage.storageUsedMB / 1024 : 0, // Store as GB number
          storageUsedDisplay: company.usage ? (company.usage.storageUsedMB / 1024).toFixed(2) : '0.00', // For display
          usersCount: company.actualUsersCount || company.usersCount || 0,
          leadsCount: company.usage ? company.usage.leadsCount : 0,
          customersCount: company.usage ? company.usage.customersCount : 0
        };
      });
      
      setCompanies(mappedCompanies);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  };

  const statuses = ['active', 'suspended', 'trial', 'expired'];

  // Table columns configuration
  const tableColumns = useMemo(() => [
    {
      key: 'name',
      header: 'Company',
      sortable: true,
      searchable: true,
      render: (value, item) => (
        <div className="flex items-center">
          <div className="h-10 w-10 rounded-lg bg-blue-500 flex items-center justify-center text-white font-bold mr-3">
            {value?.charAt(0)?.toUpperCase() || 'C'}
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900">{value}</div>
            <div className="text-sm text-gray-500">{item.email}</div>
          </div>
        </div>
      )
    },
    {
      key: 'country',
      header: 'Country',
      sortable: true,
      filterable: true,
      filterOptions: countries.map(country => ({
        value: country._id,
        label: `${country.flag} ${country.name}`
      })),
      filterFunction: (item, filterValue) => {
        return item.country && item.country._id === filterValue;
      },
      render: (value, item) => (
        <div className="text-sm text-gray-900">
          {item.country?.flag} {item.country?.name || 'Not Set'}
        </div>
      )
    },
    {
      key: 'plan',
      header: 'Plan',
      sortable: true,
      filterable: true,
      filterOptions: plans.map(plan => ({
        value: plan._id,
        label: `${plan.name} (${plan.currency?.symbol || '$'}${plan.price}/${plan.billingCycle || 'month'})`
      })),
      filterFunction: (item, filterValue) => {
        return item.plan && item.plan._id === filterValue;
      },
      render: (value, item) => (
        <div className="text-sm text-gray-900">
          {item.plan?.name || 'No Plan'}
          {item.plan && (
            <div className="text-xs text-gray-500">
              {item.plan.currency?.symbol || '$'}{item.plan.price}/{item.plan.billingCycle || 'month'}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'subscriptionStatus',
      header: 'Status',
      sortable: true,
      filterable: true,
      filterOptions: [
        { value: 'active', label: 'Active' },
        { value: 'trial', label: 'Trial' },
        { value: 'suspended', label: 'Suspended' },
        { value: 'expired', label: 'Expired' }
      ],
      render: (value) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value === 'active' ? 'bg-green-100 text-green-800' :
          value === 'trial' ? 'bg-blue-100 text-blue-800' :
          value === 'suspended' ? 'bg-yellow-100 text-yellow-800' :
          'bg-red-100 text-red-800'
        }`}>
          {value === 'active' && <CheckCircleIcon className="h-3 w-3 mr-1" />}
          {value === 'suspended' && <ExclamationTriangleIcon className="h-3 w-3 mr-1" />}
          {value === 'expired' && <XCircleIcon className="h-3 w-3 mr-1" />}
          {value?.charAt(0)?.toUpperCase() + value?.slice(1)}
        </span>
      )
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      type: 'date'
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_, item) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleViewCompany(item);
            }}
            className="text-blue-600 hover:text-blue-900"
          >
            <EyeIcon className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleEditCompany(item);
            }}
            className="text-yellow-600 hover:text-yellow-900"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteCompany(item._id);
            }}
            className="text-red-600 hover:text-red-900"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ], [countries, plans]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  // Filter plans based on selected country
  useEffect(() => {
    if (formData.country) {
      const filtered = plans.filter(plan => 
        plan.country?._id === formData.country || plan.country === formData.country
      );
      setFilteredPlans(filtered);
    } else {
      setFilteredPlans(plans);
    }
  }, [formData.country, plans]);

  const handleAddCompany = async (e) => {
    e.preventDefault();
    try {
      // Normalize customFields to server shape
      const payload = { ...formData };
      payload.customFields = (formData.customFields || []).map((f, idx) => ({
        fieldName: f.name || f.fieldName || '',
        fieldType: f.type || f.fieldType || 'text',
        module: f.module || 'lead',
        isRequired: !!f.required || !!f.isRequired,
        options: Array.isArray(f.options) ? f.options : (f.options ? [f.options] : []),
        placeholder: f.placeholder || '',
        validation: f.validation || {},
        displayOrder: f.order ?? f.displayOrder ?? idx,
        isActive: f.isActive !== false
      }));

      // Ensure industry field is passed as id or empty
      if (payload.industry && typeof payload.industry === 'object') {
        payload.industry = payload.industry._id || '';
      }

      const response = await companiesAPI.create(payload);
      const newCompany = response.data.company || response.data;
      
      // Resolve country and currency IDs to objects for proper display
      const resolvedCountry = countries.find(c => c._id === newCompany.country) || 
                             (typeof newCompany.country === 'object' ? newCompany.country : null);
      const resolvedCurrency = currencies.find(c => c._id === newCompany.currency) || 
                              (typeof newCompany.currency === 'object' ? newCompany.currency : null);
      
      const companyWithResolvedData = {
        ...newCompany,
        country: resolvedCountry,
        currency: resolvedCurrency,
        storageUsed: newCompany.usage ? newCompany.usage.storageUsedMB / 1024 : 0,
        storageUsedDisplay: newCompany.usage ? (newCompany.usage.storageUsedMB / 1024).toFixed(2) : '0.00',
        usersCount: newCompany.actualUsersCount || newCompany.usersCount || 0,
        leadsCount: newCompany.usage ? newCompany.usage.leadsCount : 0,
        customersCount: newCompany.usage ? newCompany.usage.customersCount : 0
      };
      
      setCompanies([...companies, companyWithResolvedData]);
      setShowAddModal(false);
      resetForm();
      toast.success('Company added successfully');
    } catch (error) {
      console.error('Error adding company:', error);
      toast.error('Failed to add company');
    }
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    // Map server customFields shape to client UI shape
    const mappedFields = (company.customFields || []).map((f) => ({
      name: f.fieldName || f.name || '',
      type: f.fieldType || f.type || 'text',
      module: f.module || 'lead',
      required: f.isRequired ?? f.required ?? false,
      options: Array.isArray(f.options) ? f.options : (f.options ? [f.options] : []),
      order: f.displayOrder ?? f.order ?? 0,
      placeholder: f.placeholder || '',
      validation: f.validation || {},
      isIndustry: false
    }));

    setFormData({
      name: company.name || '',
      email: company.email || '',
      phone: company.phone || '',
      address: formatAddress(company.address),
      website: company.website || '',
      country: company.country?._id || company.country || '',
      currency: company.currency?._id || company.currency || '',
      industry: company.industry?._id || company.industry || '',
      plan: company.plan?._id || company.plan || '',
      subscriptionStatus: company.subscriptionStatus || 'active',
      description: company.description || '',
      customFields: mappedFields
    });
    setShowEditModal(true);
  };

  const handleUpdateCompany = async (e) => {
    e.preventDefault();
    try {
      // Normalize customFields similar to add
      const payload = { ...formData };
      payload.customFields = (formData.customFields || []).map((f, idx) => ({
        fieldName: f.name || f.fieldName || '',
        fieldType: f.type || f.fieldType || 'text',
        module: f.module || 'lead',
        isRequired: !!f.required || !!f.isRequired,
        options: Array.isArray(f.options) ? f.options : (f.options ? [f.options] : []),
        placeholder: f.placeholder || '',
        validation: f.validation || {},
        displayOrder: f.order ?? f.displayOrder ?? idx,
        isActive: f.isActive !== false
      }));
      if (payload.industry && typeof payload.industry === 'object') {
        payload.industry = payload.industry._id || '';
      }

      const response = await companiesAPI.update(selectedCompany._id, payload);
      const updatedCompany = response.data.company || response.data;
      
      // Resolve country and currency IDs to objects for proper display
      const resolvedCountry = countries.find(c => c._id === updatedCompany.country) || 
                             (typeof updatedCompany.country === 'object' ? updatedCompany.country : null);
      const resolvedCurrency = currencies.find(c => c._id === updatedCompany.currency) || 
                              (typeof updatedCompany.currency === 'object' ? updatedCompany.currency : null);
      
      const companyWithResolvedData = {
        ...updatedCompany,
        country: resolvedCountry,
        currency: resolvedCurrency,
        storageUsed: updatedCompany.usage ? updatedCompany.usage.storageUsedMB / 1024 : 0,
        storageUsedDisplay: updatedCompany.usage ? (updatedCompany.usage.storageUsedMB / 1024).toFixed(2) : '0.00',
        usersCount: updatedCompany.actualUsersCount || updatedCompany.usersCount || 0,
        leadsCount: updatedCompany.usage ? updatedCompany.usage.leadsCount : 0,
        customersCount: updatedCompany.usage ? updatedCompany.usage.customersCount : 0
      };
      
      setCompanies(companies.map(company => 
        company._id === selectedCompany._id ? companyWithResolvedData : company
      ));
      setShowEditModal(false);
      resetForm();
      toast.success('Company updated successfully');
    } catch (error) {
      console.error('Error updating company:', error);
      toast.error('Failed to update company');
    }
  };

  const handleDeleteCompany = (companyId) => {
    const company = companies.find(c => c._id === companyId);
    setCompanyToDelete(company);
    setShowDeleteModal(true);
  };

  const confirmDeleteCompany = async () => {
    if (!companyToDelete) return;
    
    try {
      await companiesAPI.delete(companyToDelete._id);
      setCompanies(companies.filter(company => company._id !== companyToDelete._id));
      toast.success('Company deleted successfully');
    } catch (error) {
      console.error('Error deleting company:', error);
      if (error.response?.status === 400 && error.response?.data?.message?.includes('active plan')) {
        toast.error('Cannot delete company with active plan. Please contact support.');
      } else {
        toast.error('Failed to delete company');
      }
    } finally {
      setShowDeleteModal(false);
      setCompanyToDelete(null);
    }
  };

  const handleViewCompany = (company) => {
    setSelectedCompany(company);
    setShowDetailModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      website: '',
      country: '',
      currency: '',
      industry: '',
      plan: '',
      subscriptionStatus: 'active',
      description: '',
      customFields: []
    });
    setSelectedCompany(null);
    setCompanyToDelete(null);
  };

  const handleRowClick = (company) => {
    handleViewCompany(company);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div className="mb-4 sm:mb-0">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Companies Management
                </h1>
                <p className="mt-2 text-gray-600 text-lg">
                  Manage all companies, their plans, storage usage, and billing information
                </p>
              </div>
              <div className="flex-shrink-0">
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    // Reset any edit modal state before opening add modal
                    setShowEditModal(false);
                    setShowDetailModal(false);
                    setShowDeleteModal(false);
                    resetForm();
                    setShowAddModal(true);
                  }}
                  className="inline-flex items-center px-6 py-3 border border-transparent rounded-2xl shadow-lg text-base font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Company
                </motion.button>
              </div>
            </div>
          </div>

      {/* Statistics Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8"
      >
        <motion.div
          whileHover={{ y: -5, scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300 }}
          className="relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300"
        >
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Total Companies</p>
                <p className="text-white text-3xl font-bold mt-1">{companies.length}</p>
              </div>
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <BuildingOfficeIcon className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          whileHover={{ y: -5, scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300 }}
          className="relative overflow-hidden bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300"
        >
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm font-medium">Active Companies</p>
                <p className="text-white text-3xl font-bold mt-1">
                  {companies.filter(c => c.subscriptionStatus === 'active').length}
                </p>
              </div>
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <CheckCircleIcon className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          whileHover={{ y: -5, scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300 }}
          className="relative overflow-hidden bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300"
        >
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Monthly Revenue</p>
                <p className="text-white text-3xl font-bold mt-1">
                  ${companies.reduce((total, company) => {
                    const plan = plans.find(p => p.id === company.plan);
                    return total + (plan?.price || 0);
                  }, 0).toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <CurrencyDollarIcon className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          whileHover={{ y: -5, scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300 }}
          className="relative overflow-hidden bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300"
        >
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10"></div>
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm font-medium">Total Storage Used</p>
                <p className="text-white text-3xl font-bold mt-1">
                  {formatStorageSize(companies.reduce((total, company) => total + (company.storageUsed || 0), 0) * 1024 * 1024 * 1024)}
                </p>
              </div>
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <CloudArrowUpIcon className="h-8 w-8 text-white" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Companies Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-gray-100/50">
          <h2 className="text-lg font-semibold text-gray-900">Companies</h2>
          <p className="text-sm text-gray-600 mt-1">Manage all registered companies and their details</p>
        </div>

        <div className="overflow-x-auto">
          {/* Card Grid Layout */}
          {companies.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {companies.map((company, index) => (
                <motion.div
                  key={company._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="group bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-2xl border border-white/50 overflow-hidden transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                  onClick={() => handleViewCompany(company)}
                >
                  {/* Card Header */}
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 relative overflow-hidden">
                    <div className="absolute inset-0 bg-black/10"></div>
                    <div className="relative flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30">
                          <span className="text-white font-bold text-lg">
                            {company.name?.charAt(0)?.toUpperCase() || 'C'}
                          </span>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">{company.name}</h3>
                          <p className="text-white/80 text-xs">{company.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          company.subscriptionStatus === 'active'
                            ? 'bg-emerald-500/20 text-emerald-100 border border-emerald-400/30'
                            : company.subscriptionStatus === 'trial'
                            ? 'bg-blue-500/20 text-blue-100 border border-blue-400/30'
                            : company.subscriptionStatus === 'suspended'
                            ? 'bg-yellow-500/20 text-yellow-100 border border-yellow-400/30'
                            : 'bg-gray-500/20 text-gray-100 border border-gray-400/30'
                        }`}>
                          <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                            company.subscriptionStatus === 'active' ? 'bg-emerald-400' :
                            company.subscriptionStatus === 'trial' ? 'bg-blue-400' :
                            company.subscriptionStatus === 'suspended' ? 'bg-yellow-400' : 'bg-gray-400'
                          }`}></div>
                          {company.subscriptionStatus?.charAt(0)?.toUpperCase() + company.subscriptionStatus?.slice(1) || 'Unknown'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-4 space-y-3">
                    {/* Company Details */}
                    <div className="grid grid-cols-1 gap-3 text-sm">
                      <div className="bg-gray-50/50 rounded-lg p-3">
                        <p className="text-gray-500 text-xs font-medium">Country</p>
                        <p className="text-gray-900 font-semibold flex items-center">
                          <span className="mr-2">{company.country?.flag}</span>
                          {company.country?.name || 'Not Set'}
                        </p>
                      </div>
                      <div className="bg-gray-50/50 rounded-lg p-3">
                        <p className="text-gray-500 text-xs font-medium">Plan</p>
                        <p className="text-gray-900 font-semibold">
                          {company.plan?.name || 'No Plan'}
                          {company.plan && (
                            <span className="text-gray-500 text-xs ml-2">
                              ({company.plan.currency?.symbol || '$'}{company.plan.price}/{company.plan.billingCycle || 'month'})
                            </span>
                          )}
                        </p>
                      </div>
                      {company.storageUsed > 0 && (
                        <div className="bg-gray-50/50 rounded-lg p-3">
                          <p className="text-gray-500 text-xs font-medium">Storage Used</p>
                          <p className="text-gray-900 font-semibold">{formatStorageSize(company.storageUsed * 1024 * 1024 * 1024)}</p>
                        </div>
                      )}
                    </div>

                    {/* Metadata */}
                    <div className="flex items-center justify-between text-xs text-gray-500 border-t border-gray-100 pt-2">
                      <span>Created {new Date(company.createdAt).toLocaleDateString('en-GB')}</span>
                      <span>{company.users?.length || 0} users</span>
                    </div>
                  </div>

                  {/* Card Actions */}
                  <div className="px-4 pb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewCompany(company);
                          }}
                          className="flex items-center space-x-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors duration-200 text-xs font-medium"
                        >
                          <EyeIcon className="h-3.5 w-3.5" />
                          <span>View</span>
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditCompany(company);
                          }}
                          className="flex items-center space-x-1 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors duration-200 text-xs font-medium"
                        >
                          <PencilIcon className="h-3.5 w-3.5" />
                          <span>Edit</span>
                        </motion.button>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCompany(company._id);
                        }}
                        className="flex items-center space-x-1 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors duration-200 text-xs font-medium"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                        <span>Delete</span>
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 px-6">
              <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center mb-6 shadow-lg">
                <BuildingOfficeIcon className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No companies yet
              </h3>
              <p className="text-gray-500 text-center max-w-md mb-6">
                Get started by creating your first company to begin managing your business operations.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  resetForm();
                  setShowAddModal(true);
                }}
                className="inline-flex items-center px-6 py-3 border border-transparent rounded-2xl shadow-lg text-base font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-200"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Create Your First Company
              </motion.button>
            </div>
          )}
        </div>
      </motion.div>

      {/* Add Company Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100"
            >
              <form onSubmit={handleAddCompany}>
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                        <PlusIcon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white">Add New Company</h3>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAddModal(false)}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                    >
                      <XMarkIcon className="h-6 w-6 text-white" />
                    </button>
                  </div>
                </div>

                <div className="px-6 py-4 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Website
                      </label>
                      <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="https://"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Country *
                      </label>
                      <select
                        required
                        value={formData.country}
                        onChange={(e) => handleCountryChange(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Country</option>
                        {countries.map(country => (
                          <option key={country._id} value={country._id}>
                            {country.flag} {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Currency *
                      </label>
                      <select
                        required
                        value={formData.currency}
                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Currency</option>
                        {currencies.map(currency => (
                          <option key={currency._id} value={currency._id}>
                            {currency.symbol} {currency.name} ({currency.code})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Industry
                      </label>
                      <select
                        value={formData.industry}
                        onChange={(e) => handleIndustryChange(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Industry</option>
                        {industries && Array.isArray(industries) && industries.map(industry => (
                          <option key={industry._id} value={industry._id}>
                            {industry.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Plan *
                      </label>
                      <select
                        required
                        value={formData.plan}
                        onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Plan</option>
                        {filteredPlans.map(plan => (
                          <option key={plan._id} value={plan._id}>
                            {plan.name} - {plan.currency?.symbol || '$'}{plan.price}/{plan.billingCycle || 'month'}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={formData.subscriptionStatus}
                        onChange={(e) => setFormData({ ...formData, subscriptionStatus: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {statuses.map(status => (
                          <option key={status} value={status}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address
                    </label>
                    <textarea
                      rows={3}
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Company address..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      rows={4}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Company description or notes..."
                    />
                  </div>

                  {/* Custom Fields Section */}
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-100">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <PlusIcon className="h-5 w-5 text-purple-600" />
                        </div>
                        <h4 className="text-lg font-semibold text-gray-800">Custom Fields</h4>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          customFields: [...formData.customFields, {
                            name: '',
                            type: 'text',
                            module: 'lead',
                            required: false,
                            options: []
                          }]
                        })}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-200 text-sm font-medium"
                      >
                        Add Field
                      </motion.button>
                    </div>

                    <div className="space-y-4">
                      {formData.customFields.map((field, index) => (
                        <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Field Name *
                              </label>
                              <input
                                type="text"
                                value={field.name}
                                onChange={(e) => {
                                  const updatedFields = [...formData.customFields];
                                  updatedFields[index].name = e.target.value;
                                  setFormData({ ...formData, customFields: updatedFields });
                                }}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="Enter field name"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Field Type *
                              </label>
                              <select
                                value={field.type}
                                onChange={(e) => {
                                  const updatedFields = [...formData.customFields];
                                  updatedFields[index].type = e.target.value;
                                  // Reset options if not dropdown
                                  if (!['dropdown', 'multiselect'].includes(e.target.value)) {
                                    updatedFields[index].options = [];
                                  }
                                  setFormData({ ...formData, customFields: updatedFields });
                                }}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              >
                                <option value="text">Text</option>
                                <option value="number">Number</option>
                                <option value="email">Email</option>
                                <option value="phone">Phone</option>
                                <option value="date">Date</option>
                                <option value="dropdown">Dropdown</option>
                                <option value="textarea">Textarea</option>
                                <option value="checkbox">Checkbox</option>
                                <option value="url">URL</option>
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Module *
                              </label>
                              <select
                                value={field.module || 'lead'}
                                onChange={(e) => {
                                  const updatedFields = [...formData.customFields];
                                  updatedFields[index].module = e.target.value;
                                  setFormData({ ...formData, customFields: updatedFields });
                                }}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              >
                                <option value="lead">Lead</option>
                                <option value="customer">Customer</option>
                              </select>
                            </div>
                          </div>

                          {(field.type === 'dropdown' || field.type === 'multiselect') && (
                            <div className="mb-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Options
                              </label>
                              <div className="space-y-3">
                                {/* Display existing options as chips */}
                                {field.options && field.options.length > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                    {field.options.map((option, optIndex) => (
                                      <span key={optIndex} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                                        {option}
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const updatedFields = [...formData.customFields];
                                            updatedFields[index].options = updatedFields[index].options.filter((_, i) => i !== optIndex);
                                            setFormData({ ...formData, customFields: updatedFields });
                                          }}
                                          className="ml-2 text-purple-600 hover:text-purple-800 focus:outline-none"
                                        >
                                          <XMarkIcon className="h-3 w-3" />
                                        </button>
                                      </span>
                                    ))}
                                  </div>
                                )}

                                {/* Add new option input */}
                                <div className="flex space-x-2">
                                  <input
                                    type="text"
                                    placeholder="Add new option"
                                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        const value = e.target.value.trim();
                                        if (value) {
                                          const updatedFields = [...formData.customFields];
                                          if (!updatedFields[index].options) {
                                            updatedFields[index].options = [];
                                          }
                                          updatedFields[index].options.push(value);
                                          setFormData({ ...formData, customFields: updatedFields });
                                          e.target.value = '';
                                        }
                                      }
                                    }}
                                  />
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      const input = e.target.previousElementSibling;
                                      const value = input.value.trim();
                                      if (value) {
                                        const updatedFields = [...formData.customFields];
                                        if (!updatedFields[index].options) {
                                          updatedFields[index].options = [];
                                        }
                                        updatedFields[index].options.push(value);
                                        setFormData({ ...formData, customFields: updatedFields });
                                        input.value = '';
                                      }
                                    }}
                                    className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 text-sm font-medium"
                                  >
                                    Add
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id={`required-${index}`}
                                checked={field.required}
                                onChange={(e) => {
                                  const updatedFields = [...formData.customFields];
                                  updatedFields[index].required = e.target.checked;
                                  setFormData({ ...formData, customFields: updatedFields });
                                }}
                                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                              />
                              <label htmlFor={`required-${index}`} className="text-sm text-gray-700">
                                Required field
                              </label>
                            </div>

                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              type="button"
                              onClick={() => {
                                const updatedFields = formData.customFields.filter((_, i) => i !== index);
                                setFormData({ ...formData, customFields: updatedFields });
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </motion.button>
                          </div>
                        </div>
                      ))}

                      {formData.customFields.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <PlusIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                          <p className="text-sm">No custom fields added yet. Click "Add Field" to create custom fields for this company.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="px-6 py-6 bg-gray-50 rounded-b-2xl flex justify-end space-x-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 border border-transparent rounded-xl hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-lg"
                  >
                    Add Company
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Company Modal */}
      <AnimatePresence>
        {showEditModal && selectedCompany && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100"
            >
              <form onSubmit={handleUpdateCompany}>
                <div className="bg-gradient-to-r from-yellow-500 to-orange-500 px-6 py-4 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                        <PencilIcon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white">Edit Company</h3>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                    >
                      <XMarkIcon className="h-6 w-6 text-white" />
                    </button>
                  </div>
                </div>

                <div className="px-6 py-4 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Website
                      </label>
                      <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="https://"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Country *
                      </label>
                      <select
                        required
                        value={formData.country}
                        onChange={(e) => handleCountryChange(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Country</option>
                        {countries.map(country => (
                          <option key={country._id} value={country._id}>
                            {country.flag} {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Currency *
                      </label>
                      <select
                        required
                        value={formData.currency}
                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Currency</option>
                        {currencies.map(currency => (
                          <option key={currency._id} value={currency._id}>
                            {currency.symbol} {currency.name} ({currency.code})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Industry
                      </label>
                      <select
                        value={formData.industry}
                        onChange={(e) => handleIndustryChange(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Industry</option>
                        {industries && Array.isArray(industries) && industries.map(industry => (
                          <option key={industry._id} value={industry._id}>
                            {industry.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Plan *
                      </label>
                      <select
                        required
                        value={formData.plan}
                        onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Plan</option>
                        {filteredPlans.map(plan => (
                          <option key={plan._id} value={plan._id}>
                            {plan.name} - {plan.currency?.symbol || '$'}{plan.price}/{plan.billingCycle || 'month'}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={formData.subscriptionStatus}
                        onChange={(e) => setFormData({ ...formData, subscriptionStatus: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {statuses.map(status => (
                          <option key={status} value={status}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address
                    </label>
                    <textarea
                      rows={3}
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Company address..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      rows={4}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Company description or notes..."
                    />
                  </div>

                  {/* Custom Fields Section */}
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-100">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <PlusIcon className="h-5 w-5 text-purple-600" />
                        </div>
                        <h4 className="text-lg font-semibold text-gray-800">Custom Fields</h4>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          customFields: [...formData.customFields, {
                            name: '',
                            type: 'text',
                            module: 'lead',
                            required: false,
                            options: []
                          }]
                        })}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-200 text-sm font-medium"
                      >
                        Add Field
                      </motion.button>
                    </div>

                    <div className="space-y-4">
                      {formData.customFields.map((field, index) => (
                        <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Field Name *
                              </label>
                              <input
                                type="text"
                                value={field.name}
                                onChange={(e) => {
                                  const updatedFields = [...formData.customFields];
                                  updatedFields[index].name = e.target.value;
                                  setFormData({ ...formData, customFields: updatedFields });
                                }}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="Enter field name"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Field Type *
                              </label>
                              <select
                                value={field.type}
                                onChange={(e) => {
                                  const updatedFields = [...formData.customFields];
                                  updatedFields[index].type = e.target.value;
                                  // Reset options if not dropdown
                                  if (!['dropdown', 'multiselect'].includes(e.target.value)) {
                                    updatedFields[index].options = [];
                                  }
                                  setFormData({ ...formData, customFields: updatedFields });
                                }}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              >
                                <option value="text">Text</option>
                                <option value="number">Number</option>
                                <option value="email">Email</option>
                                <option value="phone">Phone</option>
                                <option value="date">Date</option>
                                <option value="dropdown">Dropdown</option>
                                <option value="textarea">Textarea</option>
                                <option value="checkbox">Checkbox</option>
                                <option value="url">URL</option>
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Module *
                              </label>
                              <select
                                value={field.module || 'lead'}
                                onChange={(e) => {
                                  const updatedFields = [...formData.customFields];
                                  updatedFields[index].module = e.target.value;
                                  setFormData({ ...formData, customFields: updatedFields });
                                }}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              >
                                <option value="lead">Lead</option>
                                <option value="customer">Customer</option>
                              </select>
                            </div>
                          </div>

                          {(field.type === 'dropdown' || field.type === 'multiselect') && (
                            <div className="mb-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Options
                              </label>
                              <div className="space-y-3">
                                {/* Display existing options as chips */}
                                {field.options && field.options.length > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                    {field.options.map((option, optIndex) => (
                                      <span key={optIndex} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                                        {option}
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const updatedFields = [...formData.customFields];
                                            updatedFields[index].options = updatedFields[index].options.filter((_, i) => i !== optIndex);
                                            setFormData({ ...formData, customFields: updatedFields });
                                          }}
                                          className="ml-2 text-purple-600 hover:text-purple-800 focus:outline-none"
                                        >
                                          <XMarkIcon className="h-3 w-3" />
                                        </button>
                                      </span>
                                    ))}
                                  </div>
                                )}

                                {/* Add new option input */}
                                <div className="flex space-x-2">
                                  <input
                                    type="text"
                                    placeholder="Add new option"
                                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        const value = e.target.value.trim();
                                        if (value) {
                                          const updatedFields = [...formData.customFields];
                                          if (!updatedFields[index].options) {
                                            updatedFields[index].options = [];
                                          }
                                          updatedFields[index].options.push(value);
                                          setFormData({ ...formData, customFields: updatedFields });
                                          e.target.value = '';
                                        }
                                      }
                                    }}
                                  />
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      const input = e.target.previousElementSibling;
                                      const value = input.value.trim();
                                      if (value) {
                                        const updatedFields = [...formData.customFields];
                                        if (!updatedFields[index].options) {
                                          updatedFields[index].options = [];
                                        }
                                        updatedFields[index].options.push(value);
                                        setFormData({ ...formData, customFields: updatedFields });
                                        input.value = '';
                                      }
                                    }}
                                    className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 text-sm font-medium"
                                  >
                                    Add
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id={`required-${index}`}
                                checked={field.required}
                                onChange={(e) => {
                                  const updatedFields = [...formData.customFields];
                                  updatedFields[index].required = e.target.checked;
                                  setFormData({ ...formData, customFields: updatedFields });
                                }}
                                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                              />
                              <label htmlFor={`required-${index}`} className="text-sm text-gray-700">
                                Required field
                              </label>
                            </div>

                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              type="button"
                              onClick={() => {
                                const updatedFields = formData.customFields.filter((_, i) => i !== index);
                                setFormData({ ...formData, customFields: updatedFields });
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </motion.button>
                          </div>
                        </div>
                      ))}

                      {formData.customFields.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <PlusIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                          <p className="text-sm">No custom fields added yet. Click "Add Field" to create custom fields for this company.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="px-6 py-6 bg-gray-50 rounded-b-2xl flex justify-end space-x-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 border border-transparent rounded-xl hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-lg"
                  >
                    Add Company
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Company Modal */}
      <AnimatePresence>
        {showEditModal && selectedCompany && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100"
            >
              <form onSubmit={handleUpdateCompany}>
                <div className="bg-gradient-to-r from-yellow-500 to-orange-500 px-6 py-4 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                        <PencilIcon className="h-6 w-6 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white">Edit Company</h3>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                    >
                      <XMarkIcon className="h-6 w-6 text-white" />
                    </button>
                  </div>
                </div>

                <div className="px-6 py-4 space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Website
                      </label>
                      <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="https://"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Country *
                      </label>
                      <select
                        required
                        value={formData.country}
                        onChange={(e) => handleCountryChange(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Country</option>
                        {countries.map(country => (
                          <option key={country._id} value={country._id}>
                            {country.flag} {country.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Currency *
                      </label>
                      <select
                        required
                        value={formData.currency}
                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Currency</option>
                        {currencies.map(currency => (
                          <option key={currency._id} value={currency._id}>
                            {currency.symbol} {currency.name} ({currency.code})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Industry
                      </label>
                      <select
                        value={formData.industry}
                        onChange={(e) => handleIndustryChange(e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Industry</option>
                        {industries && Array.isArray(industries) && industries.map(industry => (
                          <option key={industry._id} value={industry._id}>
                            {industry.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Plan *
                      </label>
                      <select
                        required
                        value={formData.plan}
                        onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Plan</option>
                        {filteredPlans.map(plan => (
                          <option key={plan._id} value={plan._id}>
                            {plan.name} - {plan.currency?.symbol || '$'}{plan.price}/{plan.billingCycle || 'month'}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={formData.subscriptionStatus}
                        onChange={(e) => setFormData({ ...formData, subscriptionStatus: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {statuses.map(status => (
                          <option key={status} value={status}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address
                    </label>
                    <textarea
                      rows={3}
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Company address..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      rows={4}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Company description or notes..."
                    />
                  </div>

                  {/* Custom Fields Section */}
                  <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 border border-purple-100">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <PlusIcon className="h-5 w-5 text-purple-600" />
                        </div>
                        <h4 className="text-lg font-semibold text-gray-800">Custom Fields</h4>
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        type="button"
                        onClick={() => setFormData({
                          ...formData,
                          customFields: [...formData.customFields, {
                            name: '',
                            type: 'text',
                            module: 'lead',
                            required: false,
                            options: []
                          }]
                        })}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-200 text-sm font-medium"
                      >
                        Add Field
                      </motion.button>
                    </div>

                    <div className="space-y-4">
                      {formData.customFields.map((field, index) => (
                        <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Field Name *
                              </label>
                              <input
                                type="text"
                                value={field.name}
                                onChange={(e) => {
                                  const updatedFields = [...formData.customFields];
                                  updatedFields[index].name = e.target.value;
                                  setFormData({ ...formData, customFields: updatedFields });
                                }}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                placeholder="Enter field name"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Field Type *
                              </label>
                              <select
                                value={field.type}
                                onChange={(e) => {
                                  const updatedFields = [...formData.customFields];
                                  updatedFields[index].type = e.target.value;
                                  // Reset options if not dropdown
                                  if (!['dropdown', 'multiselect'].includes(e.target.value)) {
                                    updatedFields[index].options = [];
                                  }
                                  setFormData({ ...formData, customFields: updatedFields });
                                }}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              >
                                <option value="text">Text</option>
                                <option value="number">Number</option>
                                <option value="email">Email</option>
                                <option value="phone">Phone</option>
                                <option value="date">Date</option>
                                <option value="dropdown">Dropdown</option>
                                <option value="textarea">Textarea</option>
                                <option value="checkbox">Checkbox</option>
                                <option value="url">URL</option>
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Module *
                              </label>
                              <select
                                value={field.module || 'lead'}
                                onChange={(e) => {
                                  const updatedFields = [...formData.customFields];
                                  updatedFields[index].module = e.target.value;
                                  setFormData({ ...formData, customFields: updatedFields });
                                }}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                              >
                                <option value="lead">Lead</option>
                                <option value="customer">Customer</option>
                              </select>
                            </div>
                          </div>

                          {(field.type === 'dropdown' || field.type === 'multiselect') && (
                            <div className="mb-4">
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Options
                              </label>
                              <div className="space-y-3">
                                {/* Display existing options as chips */}
                                {field.options && field.options.length > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                    {field.options.map((option, optIndex) => (
                                      <span key={optIndex} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                                        {option}
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const updatedFields = [...formData.customFields];
                                            updatedFields[index].options = updatedFields[index].options.filter((_, i) => i !== optIndex);
                                            setFormData({ ...formData, customFields: updatedFields });
                                          }}
                                          className="ml-2 text-purple-600 hover:text-purple-800 focus:outline-none"
                                        >
                                          <XMarkIcon className="h-3 w-3" />
                                        </button>
                                      </span>
                                    ))}
                                  </div>
                                )}

                                {/* Add new option input */}
                                <div className="flex space-x-2">
                                  <input
                                    type="text"
                                    placeholder="Add new option"
                                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                                    onKeyPress={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        const value = e.target.value.trim();
                                        if (value) {
                                          const updatedFields = [...formData.customFields];
                                          if (!updatedFields[index].options) {
                                            updatedFields[index].options = [];
                                          }
                                          updatedFields[index].options.push(value);
                                          setFormData({ ...formData, customFields: updatedFields });
                                          e.target.value = '';
                                        }
                                      }
                                    }}
                                  />
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      const input = e.target.previousElementSibling;
                                      const value = input.value.trim();
                                      if (value) {
                                        const updatedFields = [...formData.customFields];
                                        if (!updatedFields[index].options) {
                                          updatedFields[index].options = [];
                                        }
                                        updatedFields[index].options.push(value);
                                        setFormData({ ...formData, customFields: updatedFields });
                                        input.value = '';
                                      }
                                    }}
                                    className="px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 text-sm font-medium"
                                  >
                                    Add
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id={`required-${index}`}
                                checked={field.required}
                                onChange={(e) => {
                                  const updatedFields = [...formData.customFields];
                                  updatedFields[index].required = e.target.checked;
                                  setFormData({ ...formData, customFields: updatedFields });
                                }}
                                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                              />
                              <label htmlFor={`required-${index}`} className="text-sm text-gray-700">
                                Required field
                              </label>
                            </div>

                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              type="button"
                              onClick={() => {
                                const updatedFields = formData.customFields.filter((_, i) => i !== index);
                                setFormData({ ...formData, customFields: updatedFields });
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </motion.button>
                          </div>
                        </div>
                      ))}

                      {formData.customFields.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <PlusIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                          <p className="text-sm">No custom fields added yet. Click "Add Field" to create custom fields for this company.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="px-6 py-6 bg-gray-50 rounded-b-2xl flex justify-end space-x-4">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-yellow-500 to-orange-500 border border-transparent rounded-xl hover:from-yellow-600 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition-all duration-200 shadow-lg"
                  >
                    Update Company
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* View Company Modal */}
      <AnimatePresence>
        {showDetailModal && selectedCompany && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDetailModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-green-500 to-teal-500 px-6 py-4 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                      <EyeIcon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white">Company Details</h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setShowDetailModal(false);
                        handleEditCompany(selectedCompany);
                      }}
                      className="p-2 bg-white/20 backdrop-blur-sm rounded-lg hover:bg-white/30 transition-colors duration-200"
                    >
                      <PencilIcon className="h-5 w-5 text-white" />
                    </motion.button>
                    <button
                      type="button"
                      onClick={() => setShowDetailModal(false)}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors duration-200"
                    >
                      <XMarkIcon className="h-6 w-6 text-white" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-8">
                {/* Company Header Card */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-6 border border-blue-100"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                        {selectedCompany.name?.charAt(0)?.toUpperCase() || 'C'}
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-1">{selectedCompany.name}</h2>
                        <p className="text-gray-600 flex items-center">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {selectedCompany.email}
                        </p>
                        {selectedCompany.phone && (
                          <p className="text-gray-600 flex items-center mt-1">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            {selectedCompany.phone}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${
                        selectedCompany.subscriptionStatus === 'active' ? 'bg-green-100 text-green-800 border border-green-200' :
                        selectedCompany.subscriptionStatus === 'trial' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                        selectedCompany.subscriptionStatus === 'suspended' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                        'bg-red-100 text-red-800 border border-red-200'
                      }`}>
                        <div className={`w-2 h-2 rounded-full mr-2 ${
                          selectedCompany.subscriptionStatus === 'active' ? 'bg-green-500' :
                          selectedCompany.subscriptionStatus === 'trial' ? 'bg-blue-500' :
                          selectedCompany.subscriptionStatus === 'suspended' ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}></div>
                        {selectedCompany.subscriptionStatus?.charAt(0).toUpperCase() + selectedCompany.subscriptionStatus?.slice(1)}
                      </span>
                    </div>
                  </div>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Company Information */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <BuildingOfficeIcon className="h-5 w-5 text-blue-500 mr-2" />
                      Company Information
                    </h3>

                    <div className="space-y-4">
                      {selectedCompany.website && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Website</span>
                          <a
                            href={selectedCompany.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
                          >
                            Visit Website
                            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        </div>
                      )}

                      {selectedCompany.country && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Country</span>
                          <span className="text-sm text-gray-900 flex items-center">
                            {selectedCompany.country.flag && <span className="mr-2">{selectedCompany.country.flag}</span>}
                            {selectedCompany.country.name || selectedCompany.country}
                          </span>
                        </div>
                      )}

                      {selectedCompany.currency && (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Currency</span>
                          <span className="text-sm text-gray-900 flex items-center">
                            {selectedCompany.currency.symbol && <span className="mr-1 font-semibold">{selectedCompany.currency.symbol}</span>}
                            {selectedCompany.currency.name || selectedCompany.currency}
                            {selectedCompany.currency.code && <span className="text-gray-500 ml-1">({selectedCompany.currency.code})</span>}
                          </span>
                        </div>
                      )}

                      {selectedCompany.address && (
                        <div className="p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600 block mb-2">Address</span>
                          <p className="text-sm text-gray-900 leading-relaxed">
                            {formatAddress(selectedCompany.address)}
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>

                  {/* Plan & Usage Information */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 }}
                    className="space-y-6"
                  >
                    {/* Plan Information */}
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <svg className="w-5 h-5 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Plan Details
                      </h3>

                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-600">Current Plan</span>
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                            (selectedCompany.plan?.name || selectedCompany.name)?.toLowerCase().includes('enterprise') ? 'bg-purple-100 text-purple-800' :
                            (selectedCompany.plan?.name || selectedCompany.name)?.toLowerCase().includes('pro') ? 'bg-blue-100 text-blue-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {selectedCompany.plan?.name || selectedCompany.name || 'No Plan'}
                          </span>
                        </div>

                        {(selectedCompany.plan?.price || selectedCompany.price) && (
                          <div className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100">
                            <span className="text-sm font-medium text-gray-600">Cost</span>
                            <span className="text-lg font-bold text-green-700">
                              {(selectedCompany.plan?.currency?.symbol || selectedCompany.currency?.symbol || '$')}{(selectedCompany.plan?.price || selectedCompany.price)}
                              <span className="text-sm font-normal text-green-600">/{selectedCompany.plan?.billingCycle || selectedCompany.billingCycle || 'month'}</span>
                            </span>
                          </div>
                        )}

                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Users</span>
                          <div className="flex items-center space-x-2">
                            <UsersIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-sm font-semibold text-gray-900">
                              {selectedCompany.usersCount || selectedCompany.userCount || 0} / {(selectedCompany.plan?.maxUsers || selectedCompany.maxUsers || 0)}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Storage Limit</span>
                          <div className="flex items-center space-x-2">
                            <CloudArrowUpIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-sm font-semibold text-gray-900">
                              {formatStorageSize((selectedCompany.plan?.storageLimitMB || selectedCompany.storageLimitMB || 0) * 1024 * 1024)}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <span className="text-sm font-medium text-gray-600">Member Since</span>
                          <div className="flex items-center text-sm text-gray-900">
                            <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                            {new Date(selectedCompany.createdAt).toLocaleDateString('en-GB')}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Storage Usage */}
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                        <CloudArrowUpIcon className="h-5 w-5 text-indigo-500 mr-2" />
                        Storage Usage
                      </h3>

                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-600">Used Storage</span>
                          <span className="text-sm font-semibold text-gray-900">
                            {selectedCompany.storageUsed ? formatStorageSize(selectedCompany.storageUsed * 1024 * 1024 * 1024) : '0 B'}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-600">Total Limit</span>
                          <span className="text-sm font-semibold text-gray-900">
                            {formatStorageSize((selectedCompany.plan?.storageLimitMB || selectedCompany.storageLimitMB || 1024) * 1024 * 1024)}
                          </span>
                        </div>

                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Usage</span>
                            <span className={`font-semibold ${
                              ((selectedCompany.storageUsed || 0) / ((selectedCompany.plan?.storageLimitMB || selectedCompany.storageLimitMB || 1024) / 1024)) * 100 > 90 ? 'text-red-600' :
                              ((selectedCompany.storageUsed || 0) / ((selectedCompany.plan?.storageLimitMB || selectedCompany.storageLimitMB || 1024) / 1024)) * 100 > 70 ? 'text-yellow-600' :
                              'text-green-600'
                            }`}>
                              {(((selectedCompany.storageUsed || 0) / ((selectedCompany.plan?.storageLimitMB || selectedCompany.storageLimitMB || 1024) / 1024)) * 100).toFixed(1)}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${Math.min(((selectedCompany.storageUsed || 0) / ((selectedCompany.plan?.storageLimitMB || selectedCompany.storageLimitMB || 1024) / 1024)) * 100, 100)}%` }}
                              transition={{ duration: 1, delay: 0.5 }}
                              className={`h-3 rounded-full ${
                                ((selectedCompany.storageUsed || 0) / ((selectedCompany.plan?.storageLimitMB || selectedCompany.storageLimitMB || 1024) / 1024)) * 100 > 90 ? 'bg-gradient-to-r from-red-500 to-red-600' :
                                ((selectedCompany.storageUsed || 0) / ((selectedCompany.plan?.storageLimitMB || selectedCompany.storageLimitMB || 1024) / 1024)) * 100 > 70 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' :
                                'bg-gradient-to-r from-green-500 to-green-600'
                              }`}
                            />
                          </div>
                        </div>

                        {/* Additional Usage Stats */}
                        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                          <div className="text-center">
                            <div className="text-lg font-bold text-blue-600">{selectedCompany.leadsCount || 0}</div>
                            <div className="text-xs text-gray-500">Leads</div>
                          </div>
                          <div className="text-center">
                            <div className="text-lg font-bold text-green-600">{selectedCompany.customersCount || 0}</div>
                            <div className="text-xs text-gray-500">Customers</div>
                          </div>
                        </div>

                        {((selectedCompany.storageUsed || 0) / ((selectedCompany.plan?.storageLimitMB || selectedCompany.storageLimitMB || 1024) / 1024)) * 100 > 80 && (
                          <div className="flex items-start space-x-2 p-3 bg-yellow-50 border border-yellow-200 rounded-xl">
                            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-yellow-800">Storage Warning</p>
                              <p className="text-sm text-yellow-700">You're running low on storage space.</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Description */}
                {selectedCompany.description && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="mt-6 bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <svg className="w-5 h-5 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Description
                    </h3>
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <p className="text-gray-700 leading-relaxed">{selectedCompany.description}</p>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && companyToDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={() => setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-red-500 to-pink-500 px-6 py-4 rounded-t-2xl">
                <div className="flex items-center justify-center mb-4">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                    <TrashIcon className="h-8 w-8 text-white" />
                  </div>
                </div>

                <div className="text-center">
                  <h3 className="text-xl font-bold text-white mb-2">
                    Delete Company
                  </h3>
                  <p className="text-red-100 mb-4">
                    Are you sure you want to delete <strong className="text-white">{companyToDelete.name}</strong>?
                  </p>

                  {companyToDelete.plan && companyToDelete.subscriptionStatus === 'active' && (
                    <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-xl p-4 mb-4">
                      <div className="flex">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-300 mr-3 flex-shrink-0 mt-0.5" />
                        <div className="text-left">
                          <p className="text-white font-semibold">Warning!</p>
                          <p className="text-red-100 text-sm">This company has an active plan ({companyToDelete.plan?.name || companyToDelete.plan}). Deleting will cancel their subscription.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <p className="text-red-200 text-sm">
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="px-6 py-6 bg-gray-50 rounded-b-2xl flex space-x-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={confirmDeleteCompany}
                  className="flex-1 px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-red-500 to-pink-500 border border-transparent rounded-xl hover:from-red-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-all duration-200 shadow-lg"
                >
                  Delete Company
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
        </div>
      </div>
    </Layout>
  );
};

export default Companies;